import { useEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Animate 0 -> target once `active` becomes true.
 * Jumps straight to the target under prefers-reduced-motion.
 */
export function useCountUp(target, { duration = 1800, active = true } = {}) {
  const [value, setValue] = useState(0);
  const frame = useRef();
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!active) return undefined;

    if (reducedMotion) {
      setValue(target);
      return undefined;
    }

    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(target * easeOut(progress));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame.current);
  }, [target, duration, active, reducedMotion]);

  // Keep decimals only for small fractional targets (e.g. 98.6)
  return target % 1 !== 0 ? Number(value.toFixed(1)) : Math.round(value);
}

export default useCountUp;
